import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Shield, Building2, Users, LogOut, Power, Trash2, Loader, ServerCog } from "lucide-react";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { toast, Toaster } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PlatformDashboardPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delText, setDelText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/tenants`, { withCredentials: true }),
        axios.get(`${API}/platform/stats`, { withCredentials: true }),
      ]);
      setTenants(t.data); setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    navigate("/platform/login");
  };

  const toggle = async (tenant_id, active) => {
    try {
      await axios.put(`${API}/tenants/${tenant_id}/active`, { active: !active }, { withCredentials: true });
      toast.success(active ? "Workspace deaktiviert" : "Workspace aktiviert");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    if (delText !== "DELETE") return;
    setDelBusy(true);
    try {
      await axios.delete(`${API}/tenants/${confirmDel._id}`, {
        data: { confirm: "DELETE" }, withCredentials: true,
      });
      toast.success("Workspace endgültig gelöscht");
      setConfirmDel(null); setDelText("");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelBusy(false); }
  };

  if (!user?.is_platform_admin) {
    navigate("/platform/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-right" />
      {/* Topbar */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-foreground flex items-center justify-center">
              <Shield size={16} className="text-background" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">DATON Platform</h1>
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Workspace-Verwaltung</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{user.email}</span>
            <button onClick={logout} data-testid="platform-logout" className="flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary text-xs font-semibold transition-colors">
              <LogOut size={12} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard icon={Building2} label="Workspaces" value={stats.total_tenants ?? "—"} sub={`${stats.active_tenants ?? 0} aktiv`} />
          <StatCard icon={Users} label="Mitglieder gesamt" value={stats.total_users ?? "—"} sub="Über alle Workspaces" />
          <StatCard icon={ServerCog} label="Datenbank-Modus" value="Multi-Tenant" sub="Eine DB pro Workspace" />
        </div>

        <div className="bg-card border border-border shadow-soft" data-testid="platform-tenants-list">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Alle Workspaces</h2>
            <span className="text-xs text-muted-foreground">{tenants.length}</span>
          </div>
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground"><Loader size={18} className="animate-spin" /></div>
          ) : tenants.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Noch keine Workspaces</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-2.5 font-bold">Workspace</th>
                  <th className="text-left px-3 py-2.5 font-bold">Owner</th>
                  <th className="text-right px-3 py-2.5 font-bold">User</th>
                  <th className="text-right px-3 py-2.5 font-bold">Kunden</th>
                  <th className="text-left px-3 py-2.5 font-bold">Status</th>
                  <th className="text-right px-5 py-2.5 font-bold">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tenants.map(t => (
                  <tr key={t._id} className="hover:bg-muted/20" data-testid={`tenant-row-${t.slug}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{t.slug}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{t.owner_email}</td>
                    <td className="px-3 py-3 text-right font-mono">{t.user_count}</td>
                    <td className="px-3 py-3 text-right font-mono">{t.customer_count}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${t.active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                        {t.active ? "Aktiv" : "Deaktiviert"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => toggle(t._id, t.active)} title={t.active ? "Deaktivieren" : "Aktivieren"} data-testid={`tenant-toggle-${t.slug}`}
                          className="p-1.5 border border-border hover:border-primary transition-colors">
                          <Power size={13} className={t.active ? "text-emerald-600" : "text-muted-foreground"} />
                        </button>
                        <button onClick={() => { setConfirmDel(t); setDelText(""); }} title="Löschen" data-testid={`tenant-delete-${t.slug}`}
                          className="p-1.5 border border-border hover:border-destructive hover:text-destructive transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 px-4 py-3 bg-amber-500/10 border border-amber-500/30 text-xs text-amber-800 dark:text-amber-300">
          🔒 <strong>Datenschutz garantiert:</strong> Als Platform-Admin siehst du nur aggregierte Statistiken. Du hast <strong>keinen Zugriff</strong> auf Kunden, Tickets, Passwörter, Dokumente oder andere Geschäftsdaten der Workspaces.
        </div>
      </main>

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="platform-delete-modal">
          <div className="bg-card border border-border shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-2 text-destructive flex items-center gap-2"><Trash2 size={18} /> Workspace löschen</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Du löscht <strong className="text-foreground">{confirmDel.name}</strong> ({confirmDel.slug}) und alle Daten unwiderruflich. Datenbank wird gelöscht. Diese Aktion ist <strong className="text-destructive">nicht rückgängig</strong> machbar.
            </p>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Tippe <code className="text-destructive">DELETE</code> zum Bestätigen</label>
            <input data-testid="platform-delete-confirm" value={delText} onChange={e => setDelText(e.target.value)}
              className="w-full px-3 py-2 mb-4 text-sm bg-background border border-input focus:border-destructive focus:outline-none font-mono" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 border border-border hover:bg-muted text-sm">Abbrechen</button>
              <button onClick={del} disabled={delText !== "DELETE" || delBusy} data-testid="platform-delete-execute"
                className="px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40 text-sm font-semibold flex items-center gap-1.5">
                {delBusy && <Loader size={13} className="animate-spin" />} Endgültig löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border shadow-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <div className="p-2.5 rounded-md bg-foreground/10 text-foreground"><Icon size={18} /></div>
      </div>
    </div>
  );
}
